import { ChecklistImportResults } from './ChecklistImportResults';
import { rememberChecklistResultBatch } from './checklist-import-results-report';
import { CatalogDeliveryPreview } from './CatalogDeliveryPreview';
import { CatalogDeliveriesView } from './CatalogDeliveriesView';
import { useChecklistSearch } from '../checklists/useChecklistSearch';
import type * as React from 'react';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import JSZip from 'jszip';
import type { MarketplaceListingTemplate } from '@tcvs/shared';
import {
  uploadChecklistSnapshotToS3,
  useChecklistsApi,
  type ChecklistSourceBatchItem,
  type ChecklistSourceImportBatch,
  type ChecklistSourceImportJob,
  type ChecklistSourceIngestSummary,
  type ChecklistSourcePreview,
} from './checklists-api';
import {
  checklistFailureReason,
  checklistPreviewProduct,
  checklistPreviewReport,
  downloadChecklistPreview,
  type ChecklistPreviewProduct,
  type ChecklistPreviewFailure,
} from './checklist-preview-report';
import { useMarketplaceApi } from '../marketplace/marketplace-api';
import type {
  ChecklistIssue,
  ChecklistIssueStatus,
  ChecklistIssueType,
  ReviewChecklistProduct,
} from '../shared/types';

class ChecklistPreviewError extends Error {}

type ChecklistPageView = 'current' | 'issues' | 'upload' | 'deliveries' | 'dryRun' | 'results';

export function ChecklistUploadPage(): JSX.Element {
  const api = useChecklistsApi();
  const [pageView, setPageView] = useState<ChecklistPageView>('current');
  const [currentQuery, setCurrentQuery] = useState('');
  const currentSearch = useChecklistSearch(api.listCurrent, currentQuery, {
    retainItems: false,
    limit: 100,
  });
  const currentChecklists = useMemo(
    () => sortChecklistProducts(currentSearch.items),
    [currentSearch.items],
  );
  const currentLoading = currentSearch.loading && currentChecklists.length === 0;
  const currentLoadingMore = currentSearch.loading && currentChecklists.length > 0;
  const currentError = currentSearch.error;
  const currentTruncated = currentSearch.hasMore;
  const loadCurrentChecklists = currentSearch.refresh;

  return (
    <div>
      <h2>Checklists</h2>
      <div className="tcvs-checklist-view-toggle" role="tablist" aria-label="Checklist view">
        <button
          type="button"
          role="tab"
          aria-selected={pageView === 'current'}
          onClick={() => setPageView('current')}
        >
          Current checklists
          {!currentLoading && currentError === null ? ` (${currentChecklists.length})` : ''}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={pageView === 'issues'}
          onClick={() => setPageView('issues')}
        >
          Reported issues
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={pageView === 'upload'}
          onClick={() => setPageView('upload')}
        >
          Upload checklist
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={pageView === 'deliveries'}
          onClick={() => setPageView('deliveries')}
        >
          Catalog deliveries
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={pageView === 'dryRun'}
          onClick={() => setPageView('dryRun')}
        >
          Catalog dry run
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={pageView === 'results'}
          onClick={() => setPageView('results')}
        >
          Import results
        </button>
      </div>

      {pageView === 'current' ? (
        <CurrentChecklistsView
          items={currentChecklists}
          totalCount={currentChecklists.length}
          query={currentQuery}
          loading={currentLoading}
          loadingMore={currentLoadingMore}
          error={currentError}
          truncated={currentTruncated}
          onQuery={setCurrentQuery}
          onRefresh={currentError === null ? currentSearch.refresh : currentSearch.retry}
          onLoadMore={currentSearch.loadMore}
          onUpload={() => setPageView('upload')}
        />
      ) : pageView === 'issues' ? (
        <ChecklistIssuesView />
      ) : pageView === 'results' ? (
        <ChecklistImportResults />
      ) : pageView === 'deliveries' ? (
        <CatalogDeliveriesView />
      ) : pageView === 'dryRun' ? (
        <>
          <p>
            Dry runs require a complete existing catalog baseline for every selected product. An
            empty or incomplete product stops preparation before any files are staged.
          </p>
          <CatalogDeliveryPreview />
        </>
      ) : (
        <SourceSnapshotFlow
          onComplete={() => {
            void loadCurrentChecklists();
          }}
        />
      )}
    </div>
  );
}

export function SourceSnapshotFlow({ onComplete }: { onComplete: () => void }): JSX.Element {
  const api = useChecklistsApi();
  const [files, setFiles] = useState<File[]>([]);
  const [key, setKey] = useState('');
  const [preview, setPreview] = useState<ChecklistSourcePreview | null>(null);
  const [result, setResult] = useState<ChecklistSourceIngestSummary | null>(null);
  const [job, setJob] = useState<ChecklistSourceImportJob | null>(null);
  const [batch, setBatch] = useState<ChecklistSourceImportBatch | null>(null);
  const [bulkItems, setBulkItems] = useState<ChecklistSourceBatchItem[]>([]);
  const [bulkFailures, setBulkFailures] = useState<ChecklistPreviewFailure[]>([]);
  const [bulkReports, setBulkReports] = useState<ChecklistPreviewProduct[]>([]);
  const [bulkPreviewComplete, setBulkPreviewComplete] = useState(false);
  const previewGeneration = useRef(0);
  const [bulkProgress, setBulkProgress] = useState({ processed: 0, total: 0, label: '' });
  const [status, setStatus] = useState<
    'idle' | 'uploading' | 'previewing' | 'ingesting' | 'polling'
  >('idle');
  const [error, setError] = useState<string | null>(null);
  const [pollingPaused, setPollingPaused] = useState(false);
  const pollGeneration = useRef(0);
  const pendingBatchId = useRef<string | null>(null);
  useEffect(
    () => () => {
      pollGeneration.current += 1;
      previewGeneration.current += 1;
    },
    [],
  );
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [replacementConfirmed, setReplacementConfirmed] = useState(false);
  const busy = status !== 'idle';
  const bulkMode =
    files.length > 1 || files.some((candidate) => candidate.name.toLowerCase().endsWith('.zip'));

  const reset = () => {
    pollGeneration.current += 1;
    previewGeneration.current += 1;
    pendingBatchId.current = null;
    setPollingPaused(false);
    setFiles([]);
    setKey('');
    setPreview(null);
    setResult(null);
    setJob(null);
    setBatch(null);
    setBulkItems([]);
    setBulkFailures([]);
    setBulkReports([]);
    setBulkPreviewComplete(false);
    setBulkProgress({ processed: 0, total: 0, label: '' });
    setError(null);
    setStatus('idle');
    setReplaceExisting(false);
    setReplacementConfirmed(false);
    localStorage.removeItem(CHECKLIST_IMPORT_BATCH_STORAGE_KEY);
  };

  const pollJob = useCallback(
    async (jobId: string): Promise<void> => {
      const generation = ++pollGeneration.current;
      setPollingPaused(false);
      setStatus('polling');
      for (let check = 0; check < CHECKLIST_IMPORT_POLL_LIMIT; check += 1) {
        await waitForChecklistImportPoll();
        if (generation !== pollGeneration.current) return;
        const response = await api.sourceImportStatus(jobId);
        if (generation !== pollGeneration.current) return;
        if (!response.ok) {
          setStatus('idle');
          setPollingPaused(true);
          setError(`Import status failed: ${response.message ?? response.error}`);
          return;
        }
        setError(null);
        setJob(response.data);
        if (response.data.status === 'COMPLETED') {
          setStatus('idle');
          setResult(sourceImportJobSummary(response.data));
          onComplete();
          return;
        }
        if (response.data.status === 'FAILED') {
          setStatus('idle');
          setError(`Import failed: ${response.data.error ?? 'unknown import error'}`);
          return;
        }
      }
      setStatus('idle');
      setPollingPaused(true);
    },
    [api, onComplete],
  );

  const pollBatch = useCallback(
    async (batchId: string): Promise<void> => {
      pendingBatchId.current = batchId;
      const generation = ++pollGeneration.current;
      setPollingPaused(false);
      setStatus('polling');
      for (let check = 0; check < CHECKLIST_IMPORT_POLL_LIMIT; check += 1) {
        if (generation !== pollGeneration.current) return;
        const response = await api.sourceBatchStatus(batchId);
        if (generation !== pollGeneration.current) return;
        if (!response.ok) {
          setStatus('idle');
          setPollingPaused(true);
          setError(`Batch status failed: ${response.message ?? response.error}`);
          return;
        }
        setError(null);
        setBatch(response.data);
        if (
          response.data.status === 'COMPLETED' ||
          response.data.status === 'COMPLETED_WITH_ERRORS' ||
          response.data.status === 'FAILED'
        ) {
          setStatus('idle');
          rememberChecklistResultBatch(batchId);
          localStorage.removeItem(CHECKLIST_IMPORT_BATCH_STORAGE_KEY);
          if (response.data.status === 'COMPLETED') onComplete();
          return;
        }
        if (check + 1 < CHECKLIST_IMPORT_POLL_LIMIT) await waitForChecklistImportPoll();
      }
      setStatus('idle');
      setPollingPaused(true);
    },
    [api, onComplete],
  );

  useEffect(() => {
    const batchId = localStorage.getItem(CHECKLIST_IMPORT_BATCH_STORAGE_KEY);
    if (batchId !== null && batch === null && status === 'idle' && !pollingPaused) {
      void pollBatch(batchId);
    }
  }, [batch, pollBatch, pollingPaused, status]);

  const runSinglePreview = async (file: File) => {
    const generation = ++previewGeneration.current;
    setError(null);
    setPreview(null);
    setResult(null);
    setStatus('uploading');
    const uploadFile = await compressChecklistSnapshot(file);
    if (generation !== previewGeneration.current) return;
    const url = await api.uploadUrl(uploadFile.name.endsWith('.gz') ? 'json.gz' : 'json');
    if (generation !== previewGeneration.current) return;
    if (!url.ok) {
      setStatus('idle');
      setError(`${file.name}: Could not start upload: ${checklistFailureReason(url)}`);
      return;
    }
    const upload = await uploadChecklistSnapshotToS3(url.data, uploadFile);
    if (generation !== previewGeneration.current) return;
    if (!upload.ok) {
      setStatus('idle');
      setError(`${file.name}: Snapshot upload failed: ${upload.error}`);
      return;
    }
    setStatus('previewing');
    const response = await api.sourcePreview({
      key: url.data.key,
      ...(replaceExisting ? { replaceExisting: true } : {}),
    });
    if (generation !== previewGeneration.current) return;
    setStatus('idle');
    if (!response.ok) {
      setError(`${file.name}: Preview failed: ${checklistFailureReason(response)}`);
      return;
    }
    setKey(url.data.key);
    setPreview(response.data);
  };

  const runBulkPreview = async () => {
    const generation = ++previewGeneration.current;
    setError(null);
    setBulkItems([]);
    setBulkFailures([]);
    setBulkReports([]);
    setBulkPreviewComplete(false);
    setBatch(null);
    setStatus('previewing');
    let candidates: BulkSnapshotCandidate[];
    try {
      candidates = await collectBulkSnapshotCandidates(files);
      if (generation !== previewGeneration.current) return;
    } catch {
      if (generation !== previewGeneration.current) return;
      setStatus('idle');
      const reason = 'Could not read archives. Check that the selected ZIP files are valid.';
      setError(reason);
      setBulkFailures([{ file: files.map((file) => file.name).join(', '), reason }]);
      return;
    }
    setBulkProgress({ processed: 0, total: candidates.length, label: '' });
    const accepted: ChecklistSourceBatchItem[] = [];
    const failures: ChecklistPreviewFailure[] = [];
    const reports: ChecklistPreviewProduct[] = [];
    const targetIds = new Set<string>();
    for (const [index, candidate] of candidates.entries()) {
      setBulkProgress({
        processed: index,
        total: candidates.length,
        label: candidate.label,
      });
      try {
        const uploadFile = await compressChecklistSnapshot(await candidate.load());
        if (generation !== previewGeneration.current) return;
        if (uploadFile.size > CHECKLIST_SOURCE_UPLOAD_LIMIT) {
          throw new ChecklistPreviewError('compressed snapshot exceeds the 20 MB upload limit');
        }
        const url = await api.uploadUrl(uploadFile.name.endsWith('.gz') ? 'json.gz' : 'json');
        if (generation !== previewGeneration.current) return;
        if (!url.ok) throw new ChecklistPreviewError(checklistFailureReason(url));
        const upload = await uploadChecklistSnapshotToS3(url.data, uploadFile);
        if (generation !== previewGeneration.current) return;
        if (!upload.ok)
          throw new ChecklistPreviewError(
            'Snapshot upload failed. Check the connection and try again.',
          );
        const response = await api.sourcePreview({ key: url.data.key });
        if (generation !== previewGeneration.current) return;
        if (!response.ok) throw new ChecklistPreviewError(checklistFailureReason(response));
        if (targetIds.has(response.data.sourceTargetId)) {
          throw new ChecklistPreviewError(
            `duplicate source target ${response.data.sourceTargetId}`,
          );
        }
        const report = checklistPreviewProduct(candidate.label, response.data);
        targetIds.add(response.data.sourceTargetId);
        reports.push(report);
        accepted.push({
          key: url.data.key,
          fingerprint: response.data.fingerprint,
          sourceTargetId: response.data.sourceTargetId,
          rosterCount: response.data.rosterCount,
          parallelDefinitionCount: response.data.parallelDefinitionCount,
          subsetDefinitionCount: response.data.subsetDefinitionCount,
          ...(response.data.qualityWarnings === undefined
            ? {}
            : {
                qualityWarnings: {
                  missingCardNumber: response.data.qualityWarnings.missingCardNumber,
                  missingName: response.data.qualityWarnings.missingName,
                },
              }),
        });
      } catch (cause) {
        if (generation !== previewGeneration.current) return;
        failures.push({
          file: candidate.label,
          reason:
            cause instanceof ChecklistPreviewError
              ? cause.message
              : 'Could not read or upload this snapshot. Check the file and try again.',
        });
      }
      setBulkReports([...reports]);
      setBulkItems([...accepted]);
      setBulkFailures([...failures]);
      setBulkProgress({
        processed: index + 1,
        total: candidates.length,
        label: candidate.label,
      });
    }
    setBulkPreviewComplete(true);
    setStatus('idle');
    if (accepted.length === 0) {
      setError('No snapshots passed preview validation.');
    }
  };

  const runPreview = async () => {
    if (files.length === 0 || busy) return;
    if (bulkMode) {
      await runBulkPreview();
      return;
    }
    const generation = previewGeneration.current + 1;
    try {
      await runSinglePreview(files[0]!);
    } catch {
      if (generation !== previewGeneration.current) return;
      setStatus('idle');
      setError(
        `${files[0]!.name}: Could not read or upload this snapshot. Check the file and try again.`,
      );
    }
  };

  const runIngest = async () => {
    if (preview === null || key === '' || busy) return;
    setError(null);
    setStatus('ingesting');
    const response = await api.sourceIngest({
      key,
      fingerprint: preview.fingerprint,
      ...(preview.replaceExisting
        ? {
            replaceExisting: true,
            replacementToken: preview.replacement?.token,
          }
        : {}),
    });
    setStatus('idle');
    if (!response.ok) {
      setError(
        `${files[0]?.name ?? 'Snapshot'}: Import failed: ${checklistFailureReason(response)}`,
      );
      return;
    }
    if (isSourceImportJob(response.data)) {
      setJob(response.data);
      await pollJob(response.data.jobId);
      return;
    } else {
      setResult(response.data);
    }
    onComplete();
  };

  const runBulkIngest = async () => {
    if (bulkItems.length === 0 || busy) return;
    setError(null);
    setStatus('uploading');
    const fingerprint = await checklistBatchFingerprint(bulkItems);
    const manifest = {
      schemaVersion: 1 as const,
      createdAt: new Date().toISOString(),
      items: bulkItems,
    };
    const manifestFile = await compressChecklistSnapshot(
      new File([JSON.stringify(manifest)], 'checklist-import-batch.json', {
        type: 'application/json',
      }),
    );
    const url = await api.uploadUrl(manifestFile.name.endsWith('.gz') ? 'json.gz' : 'json');
    if (!url.ok) {
      setStatus('idle');
      setError(`Could not upload batch manifest: ${url.message ?? url.error}`);
      return;
    }
    const upload = await uploadChecklistSnapshotToS3(url.data, manifestFile);
    if (!upload.ok) {
      setStatus('idle');
      setError(`Batch manifest upload failed: ${upload.error}`);
      return;
    }
    const response = await api.sourceBatchIngest({ key: url.data.key, fingerprint });
    if (!response.ok) {
      setStatus('idle');
      setError(`Batch import failed: ${response.message ?? response.error}`);
      return;
    }
    setBatch(response.data);
    localStorage.setItem(CHECKLIST_IMPORT_BATCH_STORAGE_KEY, response.data.batchId);
    await pollBatch(response.data.batchId);
  };

  const activeResult = result !== null || batch?.status === 'COMPLETED';

  return (
    <section className="tcvs-card">
      <h3>Source snapshot</h3>
      <p className="tcvs-muted">
        Upload one source snapshot or select JSON and ZIP archives for a bulk preview. Every
        checklist keeps its own validated fingerprint and import record.
      </p>

      {!activeResult && batch === null ? (
        <>
          <label className="tcvs-field">
            <span>Snapshot JSON or ZIP archives</span>
            <input
              type="file"
              accept=".json,.json.gz,.zip,application/json,application/zip"
              multiple
              disabled={busy || preview !== null || bulkItems.length > 0}
              onChange={(event) => {
                pollGeneration.current += 1;
                previewGeneration.current += 1;
                pendingBatchId.current = null;
                setPollingPaused(false);
                localStorage.removeItem(CHECKLIST_IMPORT_BATCH_STORAGE_KEY);
                setStatus('idle');
                setBulkProgress({ processed: 0, total: 0, label: '' });
                setFiles(Array.from(event.target.files ?? []));
                setPreview(null);
                setResult(null);
                setJob(null);
                setBatch(null);
                setBulkItems([]);
                setBulkFailures([]);
                setBulkReports([]);
                setBulkPreviewComplete(false);
                setError(null);
                setReplacementConfirmed(false);
              }}
            />
          </label>
          {!bulkMode ? (
            <>
              <label className="tcvs-image-issue-toggle">
                <input
                  type="checkbox"
                  checked={replaceExisting}
                  disabled={busy || preview !== null}
                  onChange={(event) => {
                    setReplaceExisting(event.target.checked);
                    setReplacementConfirmed(false);
                  }}
                />
                <span>Replace existing data for this source target</span>
              </label>
              <p className="tcvs-muted">
                Use replacement only for a corrected checklist. Preview identifies target-owned
                stale records and duplicate product mappings before anything is removed.
              </p>
            </>
          ) : (
            <p className="tcvs-muted">
              Bulk import is additive and non-destructive. Corrected-target replacement remains a
              separate, single-snapshot action.
            </p>
          )}
        </>
      ) : null}

      {error !== null ? (
        <div className="tcvs-alert tcvs-alert-danger" style={{ whiteSpace: 'pre-wrap' }}>
          {error}
        </div>
      ) : null}

      {preview === null && result === null && bulkItems.length === 0 && batch === null ? (
        <div className="tcvs-actions">
          <button
            type="button"
            className="tcvs-button tcvs-button-primary"
            disabled={files.length === 0 || busy}
            onClick={() => void runPreview()}
          >
            {status === 'uploading'
              ? 'Uploading...'
              : status === 'previewing'
                ? `Previewing ${bulkProgress.processed}/${bulkProgress.total}...`
                : bulkMode
                  ? 'Preview archives'
                  : 'Preview snapshot'}
          </button>
          {bulkProgress.label !== '' && busy ? (
            <span className="tcvs-muted">{bulkProgress.label}</span>
          ) : null}
        </div>
      ) : null}

      {preview !== null && result === null ? (
        <>
          <SourceSnapshotSummary preview={preview} />
          {preview.replaceExisting ? (
            <label className="tcvs-image-issue-toggle">
              <input
                type="checkbox"
                checked={replacementConfirmed}
                disabled={busy || (preview.replacement?.ownershipConflicts ?? 0) > 0}
                onChange={(event) => setReplacementConfirmed(event.target.checked)}
              />
              <span>I reviewed the replacement removals for target {preview.sourceTargetId}</span>
            </label>
          ) : null}
          <div className="tcvs-actions">
            <button
              type="button"
              className={
                preview.replaceExisting
                  ? 'tcvs-button tcvs-btn-danger'
                  : 'tcvs-button tcvs-button-primary'
              }
              disabled={
                busy ||
                (preview.replaceExisting &&
                  (!replacementConfirmed || (preview.replacement?.ownershipConflicts ?? 0) > 0))
              }
              onClick={() => void runIngest()}
            >
              {status === 'ingesting'
                ? preview.replaceExisting
                  ? 'Replacing...'
                  : 'Importing...'
                : preview.replaceExisting
                  ? `Replace target ${preview.sourceTargetId}`
                  : `Import ${preview.rosterCount} roster records + ${preview.parallelDefinitionCount} parallel definitions`}
            </button>
            <button type="button" className="tcvs-btn-secondary" disabled={busy} onClick={reset}>
              Choose another snapshot
            </button>
          </div>
        </>
      ) : null}

      {preview === null &&
      result === null &&
      (bulkItems.length > 0 || bulkFailures.length > 0 || bulkPreviewComplete) &&
      batch === null ? (
        <>
          <BulkSourceSnapshotSummary
            items={bulkItems}
            reports={bulkReports}
            complete={bulkPreviewComplete}
            failures={bulkFailures}
            progress={bulkProgress}
          />
          <div className="tcvs-actions">
            <button
              type="button"
              className="tcvs-button tcvs-button-primary"
              disabled={busy || bulkItems.length === 0}
              onClick={() => void runBulkIngest()}
            >
              Import {bulkItems.length} validated checklists
            </button>
            <button type="button" className="tcvs-btn-secondary" disabled={busy} onClick={reset}>
              Choose other archives
            </button>
          </div>
        </>
      ) : null}

      {batch === null && pendingBatchId.current !== null ? (
        <div
          className="tcvs-alert"
          style={{ height: '4rem', display: 'flex', alignItems: 'center', gap: '1rem' }}
        >
          <span style={{ flex: 1 }}>
            {pollingPaused
              ? 'Import continues in the background.'
              : 'Checking import status automatically.'}
          </span>
          <button
            type="button"
            className="tcvs-btn-secondary"
            disabled={!pollingPaused}
            onClick={() => void pollBatch(pendingBatchId.current!)}
            style={{ flexShrink: 0 }}
          >
            Check status
          </button>
        </div>
      ) : null}

      {job !== null && result === null ? (
        <ImportProgress
          label={`Importing target ${job.sourceTargetId}`}
          status={job.status}
          processed={job.progress?.processed ?? 0}
          total={job.progress?.total ?? job.rosterCount}
          pollingPaused={pollingPaused}
          onCheckStatus={job.status === 'FAILED' ? undefined : () => void pollJob(job.jobId)}
          retryDetails={
            job.status === 'RETRYING'
              ? [{ sourceTargetId: job.sourceTargetId, reason: job.error ?? '', failedAt: '' }]
              : []
          }
        />
      ) : null}

      {batch !== null &&
      !['COMPLETED', 'COMPLETED_WITH_ERRORS', 'FAILED'].includes(batch.status) ? (
        <ImportProgress
          label={`Checklist batch ${batch.status.toLowerCase().replaceAll('_', ' ')}`}
          status={batch.status}
          processed={batch.completed + batch.failed}
          total={batch.total}
          batch={batch}
          pollingPaused={pollingPaused}
          onCheckStatus={() => void pollBatch(batch.batchId)}
        />
      ) : null}

      {result !== null ? (
        <>
          <p className="tcvs-success-text">
            {result.alreadyCommitted
              ? 'This exact snapshot was already imported.'
              : result.replaceExisting
                ? `Replaced target data with ${result.rosterCount} roster records and ${result.parallelDefinitionCount} parallel definitions successfully.`
                : `Imported ${result.rosterCount} roster records and ${result.parallelDefinitionCount} parallel definitions successfully.`}
          </p>
          <dl className="tcvs-detail-list">
            <div>
              <dt>Fingerprint</dt>
              <dd className="tcvs-mono">{result.fingerprint}</dd>
            </div>
            {result.replaceExisting && result.replacement !== undefined ? (
              <div>
                <dt>Removed</dt>
                <dd>
                  {result.replacement.rosterDeletes} roster, {result.replacement.parallelDeletes}{' '}
                  parallel, {result.replacement.productDirectoryDeletes} duplicate product
                </dd>
              </div>
            ) : (
              <div>
                <dt>Source missing</dt>
                <dd>
                  {result.diff.sourceMissing} roster, {result.parallelDiff.sourceMissing} parallel
                  retained
                </dd>
              </div>
            )}
          </dl>
          <button type="button" className="tcvs-btn-secondary" onClick={reset}>
            Import another snapshot
          </button>
        </>
      ) : null}

      {batch?.status === 'COMPLETED' ? (
        <>
          <p className="tcvs-success-text">
            Imported all {batch.completed} validated checklists successfully.
          </p>
          <button type="button" className="tcvs-btn-secondary" onClick={reset}>
            Import other archives
          </button>
        </>
      ) : null}

      {batch?.status === 'COMPLETED_WITH_ERRORS' || batch?.status === 'FAILED' ? (
        <>
          <div className="tcvs-alert tcvs-alert-danger">
            <strong>
              {batch.status === 'FAILED'
                ? 'Checklist batch failed.'
                : 'Checklist batch completed with errors.'}
            </strong>
            <div>
              {batch.completed} imported, {batch.failed} failed
              {batch.error === undefined ? '' : ` · ${batch.error}`}
            </div>
          </div>
          <button type="button" className="tcvs-btn-secondary" onClick={reset}>
            Start another preview
          </button>
        </>
      ) : null}
    </section>
  );
}

const CHECKLIST_SOURCE_UPLOAD_LIMIT = 20 * 1024 * 1024;
const CHECKLIST_IMPORT_POLL_LIMIT = 150;
const CHECKLIST_IMPORT_BATCH_STORAGE_KEY = 'tcvs.checklists.activeImportBatch';

interface BulkSnapshotCandidate {
  label: string;
  load: () => Promise<File>;
}

async function collectBulkSnapshotCandidates(
  files: readonly File[],
): Promise<BulkSnapshotCandidate[]> {
  const candidates: BulkSnapshotCandidate[] = [];
  for (const file of files) {
    if (!file.name.toLowerCase().endsWith('.zip')) {
      candidates.push({ label: file.name, load: async () => file });
      continue;
    }
    const archive = await JSZip.loadAsync(file);
    const entries = Object.values(archive.files)
      .filter(
        (entry) =>
          !entry.dir &&
          entry.name.toLowerCase().endsWith('.json') &&
          !entry.name.startsWith('__MACOSX/') &&
          !entry.name.split('/').pop()?.startsWith('._'),
      )
      .sort((left, right) => left.name.localeCompare(right.name));
    for (const entry of entries) {
      candidates.push({
        label: `${file.name} / ${entry.name}`,
        load: async () =>
          new File(
            [await entry.async('uint8array')],
            entry.name.split('/').pop() ?? 'snapshot.json',
            { type: 'application/json' },
          ),
      });
    }
  }
  return candidates;
}

async function compressChecklistSnapshot(file: File): Promise<File> {
  if (file.name.toLowerCase().endsWith('.gz')) return file;
  if (typeof CompressionStream === 'undefined') {
    if (file.size <= CHECKLIST_SOURCE_UPLOAD_LIMIT) return file;
    throw new Error('This browser cannot compress snapshots larger than 20 MB.');
  }
  const stream = file.stream().pipeThrough(new CompressionStream('gzip'));
  const compressed = await new Response(stream).blob();
  return new File([compressed], `${file.name}.gz`, { type: 'application/gzip' });
}

export function canonicalChecklistBatchItems(
  items: readonly ChecklistSourceBatchItem[],
): ChecklistSourceBatchItem[] {
  return [...items]
    .map((item) => ({
      key: item.key,
      fingerprint: item.fingerprint,
      sourceTargetId: item.sourceTargetId,
      rosterCount: item.rosterCount,
      parallelDefinitionCount: item.parallelDefinitionCount,
      subsetDefinitionCount: item.subsetDefinitionCount,
    }))
    .sort(
      (left, right) =>
        compareCanonicalStrings(left.sourceTargetId, right.sourceTargetId) ||
        compareCanonicalStrings(left.fingerprint, right.fingerprint) ||
        compareCanonicalStrings(left.key, right.key),
    );
}

function compareCanonicalStrings(left: string, right: string): number {
  return left < right ? -1 : left > right ? 1 : 0;
}

export async function checklistBatchFingerprint(
  items: readonly ChecklistSourceBatchItem[],
): Promise<string> {
  const payload = new TextEncoder().encode(JSON.stringify(canonicalChecklistBatchItems(items)));
  const digest = await crypto.subtle.digest('SHA-256', payload);
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
}

function isSourceImportJob(
  value: ChecklistSourceIngestSummary | ChecklistSourceImportJob,
): value is ChecklistSourceImportJob {
  return 'jobId' in value;
}

function sourceImportJobSummary(job: ChecklistSourceImportJob): ChecklistSourceIngestSummary {
  return {
    fingerprint: job.fingerprint,
    rosterCount: job.rosterCount,
    parallelDefinitionCount: job.parallelDefinitionCount,
    subsetDefinitionCount: job.subsetDefinitionCount,
    diff: job.diff ?? { adds: 0, updates: 0, unchanged: job.rosterCount, sourceMissing: 0 },
    parallelDiff: job.parallelDiff ?? {
      adds: 0,
      updates: 0,
      unchanged: job.parallelDefinitionCount,
      sourceMissing: 0,
    },
    subsetDiff: job.subsetDiff ?? {
      adds: 0,
      updates: 0,
      unchanged: job.subsetDefinitionCount,
      sourceMissing: 0,
    },
    replaceExisting: job.replaceExisting ?? false,
    ...(job.replacement === undefined ? {} : { replacement: job.replacement }),
    alreadyCommitted: false,
  };
}

function waitForChecklistImportPoll(): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, 2_000));
}

export function ImportProgress({
  label,
  status,
  processed,
  total,
  batch,
  retryDetails = batch?.retryDetails ?? [],
  pollingPaused = false,
  onCheckStatus,
}: {
  label: string;
  status: string;
  processed: number;
  total: number;
  batch?: ChecklistSourceImportBatch;
  retryDetails?: NonNullable<ChecklistSourceImportBatch['retryDetails']>;
  pollingPaused?: boolean;
  onCheckStatus?: () => void;
}): JSX.Element {
  const terminal = ['COMPLETED', 'COMPLETED_WITH_ERRORS', 'FAILED'].includes(status);
  const retrying = terminal
    ? 0
    : (batch?.retrying ??
      (status !== 'RETRYING' ? 0 : batch === undefined ? retryDetails.length : undefined));
  const waiting = !terminal && (status === 'RETRYING' || (retrying ?? 0) > 0);
  const waitingCount = retrying ?? 0;
  const details = waiting ? retryDetails : [];
  return (
    <div className="tcvs-alert">
      <strong>{waiting ? 'Waiting to retry' : label}</strong>
      <div>
        {processed.toLocaleString()} / {total.toLocaleString()} ·{' '}
        {waiting ? 'Automatic retry pending' : status}
      </div>
      <progress
        aria-label={batch === undefined ? 'Imported records' : 'Finished checklists'}
        value={processed}
        max={Math.max(total, 1)}
        style={{ width: '100%' }}
      />
      {batch === undefined ? null : (
        <div>
          {batch.completed.toLocaleString()} imported · {batch.failed.toLocaleString()} failed ·{' '}
          {Math.max(0, batch.running - waitingCount).toLocaleString()}{' '}
          {waiting && retrying === undefined ? 'active (including retrying)' : 'running'} ·{' '}
          {batch.queued.toLocaleString()} queued ·{' '}
          {retrying === undefined
            ? 'Waiting to retry'
            : `${retrying.toLocaleString()} waiting to retry`}
        </div>
      )}
      <div
        role="status"
        aria-live="polite"
        aria-label="Import retry details"
        style={{ height: '6rem', overflowY: 'auto', overflowWrap: 'anywhere' }}
      >
        {waiting ? (
          <>
            <div>The existing import will retry automatically. Do not upload it again.</div>
            {details.length === 0 ? (
              <div>The previous attempt failed; waiting for the automatic retry.</div>
            ) : (
              <ul style={{ margin: '0.25rem 0', paddingLeft: '1.25rem' }}>
                {details.map((detail) => (
                  <li key={detail.sourceTargetId}>
                    Target {detail.sourceTargetId}: {readableImportRetryReason(detail.reason)}
                  </li>
                ))}
              </ul>
            )}
            {waitingCount > details.length && details.length > 0 ? (
              <div>{waitingCount - details.length} additional checklists waiting to retry.</div>
            ) : null}
          </>
        ) : null}
      </div>
      {onCheckStatus === undefined ? null : (
        <div style={{ height: '4rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <span style={{ flex: 1 }}>
            {pollingPaused
              ? 'Import continues in the background.'
              : 'Checking import status automatically.'}
          </span>
          <button
            type="button"
            className="tcvs-btn-secondary"
            disabled={!pollingPaused}
            onClick={onCheckStatus}
            style={{ flexShrink: 0 }}
          >
            Check status
          </button>
        </div>
      )}
    </div>
  );
}

function readableImportRetryReason(reason: string): string {
  const trimmed = reason.trim();
  if (/transaction[\s_-]*conflict/i.test(trimmed)) return 'Temporary database contention.';
  return trimmed || 'The previous attempt failed; waiting for the automatic retry.';
}

function BulkSourceSnapshotSummary({
  items,
  failures,
  reports,
  complete,
  progress,
}: {
  items: readonly ChecklistSourceBatchItem[];
  reports: ChecklistPreviewProduct[];
  complete: boolean;
  failures: ChecklistPreviewFailure[];
  progress: { processed: number; total: number; label: string };
}): JSX.Element {
  const missingCardNumber = items.reduce(
    (sum, item) => sum + (item.qualityWarnings?.missingCardNumber ?? 0),
    0,
  );
  const missingName = items.reduce(
    (sum, item) => sum + (item.qualityWarnings?.missingName ?? 0),
    0,
  );
  return (
    <>
      <dl className="tcvs-detail-list">
        <div>
          <dt>Validated checklists</dt>
          <dd>{items.length.toLocaleString()}</dd>
        </div>
        <div>
          <dt>Roster records</dt>
          <dd>{items.reduce((sum, item) => sum + item.rosterCount, 0).toLocaleString()}</dd>
        </div>
        <div>
          <dt>Parallel definitions</dt>
          <dd>
            {items.reduce((sum, item) => sum + item.parallelDefinitionCount, 0).toLocaleString()}
          </dd>
        </div>
        <div>
          <dt>Subset definitions</dt>
          <dd>
            {items.reduce((sum, item) => sum + item.subsetDefinitionCount, 0).toLocaleString()}
          </dd>
        </div>
        {missingCardNumber > 0 ? (
          <div>
            <dt>Missing card number</dt>
            <dd>{missingCardNumber.toLocaleString()} accepted with warning</dd>
          </div>
        ) : null}
        {missingName > 0 ? (
          <div>
            <dt>Missing name</dt>
            <dd>{missingName.toLocaleString()} accepted with warning</dd>
          </div>
        ) : null}
        <div>
          <dt>Files inspected</dt>
          <dd>
            {progress.processed.toLocaleString()} / {progress.total.toLocaleString()}
          </dd>
        </div>
      </dl>
      <p className="tcvs-muted">
        Preview only. Source-missing records are retained. Stored-state verification has not run.
      </p>
      <button
        type="button"
        className="tcvs-btn-secondary"
        onClick={() =>
          downloadChecklistPreview(
            checklistPreviewReport(reports, failures, complete),
            'catalog-import-preview.json',
          )
        }
      >
        Download all preview results (JSON)
      </button>
      <div style={{ maxHeight: '28rem', overflow: 'auto' }}>
        {reports.map((report) => (
          <details key={report.sourceTargetId}>
            <summary>
              {report.file} · Target {report.sourceTargetId} · {report.product.year}{' '}
              {report.product.brand} {report.product.product}
            </summary>
            <p className="tcvs-mono">Fingerprint: {report.fingerprint}</p>
            <table className="tcvs-table">
              <thead>
                <tr>
                  <th>Records</th>
                  <th>Add</th>
                  <th>Update</th>
                  <th>Unchanged</th>
                  <th>Source missing (retained)</th>
                </tr>
              </thead>
              <tbody>
                {(
                  [
                    ['Roster', report.roster],
                    ['Parallels', report.parallels],
                    ['Subsets', report.subsets],
                  ] as const
                ).map(([label, counts]) => (
                  <tr key={label}>
                    <th scope="row">{label}</th>
                    {counts ? (
                      [counts.adds, counts.updates, counts.unchanged, counts.sourceMissing].map(
                        (count, index) => <td key={index}>{count.toLocaleString()}</td>,
                      )
                    ) : (
                      <td colSpan={4}>Counts unavailable</td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            <button
              type="button"
              className="tcvs-btn-secondary"
              onClick={() =>
                downloadChecklistPreview(
                  checklistPreviewReport([report], [], true),
                  `catalog-preview-${report.sourceTargetId.replace(/[^a-zA-Z0-9_-]/g, '_')}.json`,
                )
              }
            >
              Download target {report.sourceTargetId} preview (JSON)
            </button>
          </details>
        ))}
      </div>
      {failures.length > 0 ? (
        <details className="tcvs-alert tcvs-alert-danger">
          <summary>{failures.length} snapshots could not be previewed</summary>
          <ul>
            {failures.map((failure) => (
              <li key={failure.file} style={{ whiteSpace: 'pre-wrap' }}>
                {failure.file}: {failure.reason}
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </>
  );
}

function SourceSnapshotSummary({ preview }: { preview: ChecklistSourcePreview }): JSX.Element {
  const rows = sourceSnapshotSummaryRows(preview);
  const qualityWarningCount =
    (preview.qualityWarnings?.missingCardNumber ?? 0) + (preview.qualityWarnings?.missingName ?? 0);
  return (
    <>
      <p className="tcvs-muted">
        {preview.replaceExisting
          ? 'Preview only. Replacement removes only the target-owned records listed below; existing cards and import history are retained.'
          : 'Preview only. Missing source records will be retained; this import performs no deletions.'}
      </p>
      <dl className="tcvs-detail-list">
        <div>
          <dt>Product</dt>
          <dd>
            {preview.product.year} {preview.product.brand} {preview.product.product}
          </dd>
        </div>
        <div>
          <dt>Source target</dt>
          <dd className="tcvs-mono">{preview.sourceTargetId}</dd>
        </div>
        {preview.marketplace !== undefined ? (
          <div>
            <dt>eBay category</dt>
            <dd>
              {preview.marketplace.categoryType === 'non_sport' ? 'Non-sport' : 'Sport'} singles (
              {preview.marketplace.categoryId})
            </dd>
          </div>
        ) : null}
        {preview.marketplace !== undefined &&
        Object.keys(preview.marketplace.aspects).length > 0 ? (
          <div>
            <dt>eBay item specifics</dt>
            <dd>
              {Object.entries(preview.marketplace.aspects)
                .map(([name, value]) => `${name}: ${value}`)
                .join(' · ')}
            </dd>
          </div>
        ) : null}
        <div>
          <dt>Fingerprint</dt>
          <dd className="tcvs-mono">{preview.fingerprint}</dd>
        </div>
      </dl>
      {qualityWarningCount > 0 ? (
        <details className="tcvs-alert tcvs-alert-warning">
          <summary>
            {qualityWarningCount.toLocaleString()} incomplete source field
            {qualityWarningCount === 1 ? '' : 's'} accepted with warning
          </summary>
          <p>
            These source rows remain available for operator review. TCVS did not invent the missing
            values.
          </p>
          {(preview.qualityWarnings?.samples.length ?? 0) > 0 ? (
            <ul>
              {preview.qualityWarnings?.samples.map((warning) => (
                <li key={`${warning.sourceItemId}:${warning.field}`}>
                  Source item <span className="tcvs-mono">{warning.sourceItemId}</span> is missing{' '}
                  {warning.field === 'number' ? 'a printed card number' : 'a sourced display name'}.
                </li>
              ))}
            </ul>
          ) : null}
        </details>
      ) : null}
      {preview.replacement?.duplicateProducts.length ? (
        <div className="tcvs-alert tcvs-alert-danger">
          <strong>Duplicate product mappings to remove</strong>
          <ul>
            {preview.replacement.duplicateProducts.map((product) => (
              <li key={product.checklistId}>
                {product.year} {product.brand} {product.product} ({product.rosterRecords} roster
                records)
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {(preview.replacement?.ownershipConflicts ?? 0) > 0 ? (
        <div className="tcvs-alert tcvs-alert-danger">
          Replacement is blocked because {preview.replacement?.ownershipConflicts} roster records
          could not be proven to belong to this source target.
        </div>
      ) : null}
      <div className="tcvs-table-wrap">
        <table className="tcvs-table">
          <thead>
            <tr>
              <th>Result</th>
              <th>Records</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.label}>
                <td>{row.label}</td>
                <td>{row.count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

export function sourceSnapshotSummaryRows(
  preview: ChecklistSourcePreview,
): { label: string; count: number }[] {
  if (preview.replaceExisting && preview.replacement !== undefined) {
    return [
      { label: 'Roster · Add', count: preview.diff.adds },
      { label: 'Roster · Update', count: preview.diff.updates },
      { label: 'Roster · Unchanged', count: preview.diff.unchanged },
      { label: 'Roster · Remove', count: preview.replacement.rosterDeletes },
      { label: 'Source provenance · Remove', count: preview.replacement.sourceRecordDeletes },
      { label: 'Roster · Ownership conflicts', count: preview.replacement.ownershipConflicts },
      { label: 'Parallels · Add', count: preview.parallelDiff.adds },
      { label: 'Parallels · Update', count: preview.parallelDiff.updates },
      { label: 'Parallels · Unchanged', count: preview.parallelDiff.unchanged },
      { label: 'Parallels · Remove', count: preview.replacement.parallelDeletes },
      { label: 'Subsets · Add', count: preview.subsetDiff.adds },
      { label: 'Subsets · Update', count: preview.subsetDiff.updates },
      { label: 'Subsets · Unchanged', count: preview.subsetDiff.unchanged },
      { label: 'Subsets · Remove', count: preview.replacement.subsetDeletes },
      {
        label: 'Duplicate products · Remove',
        count: preview.replacement.productDirectoryDeletes,
      },
    ];
  }
  return [
    { label: 'Roster · Add', count: preview.diff.adds },
    { label: 'Roster · Update', count: preview.diff.updates },
    { label: 'Roster · Unchanged', count: preview.diff.unchanged },
    { label: 'Roster · Source missing (retained)', count: preview.diff.sourceMissing },
    ...((preview.qualityWarnings?.missingCardNumber ?? 0) > 0
      ? [
          {
            label: 'Roster · Missing card number (accepted)',
            count: preview.qualityWarnings?.missingCardNumber ?? 0,
          },
        ]
      : []),
    ...((preview.qualityWarnings?.missingName ?? 0) > 0
      ? [
          {
            label: 'Roster · Missing name (accepted)',
            count: preview.qualityWarnings?.missingName ?? 0,
          },
        ]
      : []),
    { label: 'Parallels · Add', count: preview.parallelDiff.adds },
    { label: 'Parallels · Update', count: preview.parallelDiff.updates },
    { label: 'Parallels · Unchanged', count: preview.parallelDiff.unchanged },
    {
      label: 'Parallels · Source missing (retained)',
      count: preview.parallelDiff.sourceMissing,
    },
    { label: 'Subsets · Add', count: preview.subsetDiff.adds },
    { label: 'Subsets · Update', count: preview.subsetDiff.updates },
    { label: 'Subsets · Unchanged', count: preview.subsetDiff.unchanged },
    {
      label: 'Subsets · Source missing (retained)',
      count: preview.subsetDiff.sourceMissing,
    },
  ];
}

interface CurrentChecklistsViewProps {
  items: readonly ReviewChecklistProduct[];
  totalCount: number;
  query: string;
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  truncated: boolean;
  onQuery: (query: string) => void;
  onRefresh: () => void;
  onLoadMore: () => void;
  onUpload: () => void;
}

const CHECKLIST_ISSUE_LABELS: Record<ChecklistIssueType, string> = {
  MISSING_CARD: 'Card is missing',
  INCORRECT_CARD: 'Card details are incorrect',
  MISSING_SUBSET: 'Set/subset is missing',
  INCORRECT_SUBSET: 'Set/subset is incorrect',
  MISSING_PARALLEL: 'Parallel is missing',
  INCORRECT_PARALLEL: 'Parallel is incorrect',
  OTHER: 'Other checklist issue',
};

export function checklistIssueTypeLabel(issueType: ChecklistIssueType): string {
  return CHECKLIST_ISSUE_LABELS[issueType];
}

export function checklistIssueAnalysisActionLabel(
  issue: Pick<ChecklistIssue, 'analysis'>,
  isAnalyzing: boolean,
): string {
  if (isAnalyzing) return 'Analyzing…';
  return issue.analysis === undefined ? 'Analyze' : 'Re-analyze';
}

function checklistIssueContextLabel(issue: ChecklistIssue): string {
  const details = [
    issue.context.checklistLabel ?? issue.context.checklistId,
    issue.context.cardNumber === undefined ? undefined : `Card ${issue.context.cardNumber}`,
    issue.context.subset === undefined ? undefined : `Set/subset: ${issue.context.subset}`,
    issue.context.parallel === undefined ? undefined : `Parallel: ${issue.context.parallel}`,
  ].filter((value): value is string => value !== undefined);
  return details.length === 0 ? 'No checklist context captured' : details.join(' · ');
}

function ChecklistIssuesView(): JSX.Element {
  const api = useChecklistsApi();
  const [status, setStatus] = useState<ChecklistIssueStatus>('OPEN');
  const [items, setItems] = useState<ChecklistIssue[] | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [updating, setUpdating] = useState<string | null>(null);
  const [analyzing, setAnalyzing] = useState<string | null>(null);
  const [guidanceDrafts, setGuidanceDrafts] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    setItems(null);
    setError(null);
    const result = await api.listIssues(status);
    if (!result.ok) {
      setError(result.message ?? result.error);
      setItems([]);
      return;
    }
    setItems(result.data.items);
    setTruncated(result.data.truncated);
  }, [api, status]);

  useEffect(() => {
    void load();
  }, [load]);

  const changeStatus = async (issue: ChecklistIssue) => {
    const nextStatus: ChecklistIssueStatus = issue.status === 'OPEN' ? 'RESOLVED' : 'OPEN';
    setUpdating(issue.issueKey);
    setError(null);
    const result = await api.setIssueStatus(issue.issueKey, nextStatus);
    setUpdating(null);
    if (!result.ok) {
      setError(result.message ?? result.error);
      return;
    }
    await load();
  };

  const analyzeIssue = async (issue: ChecklistIssue): Promise<boolean> => {
    setAnalyzing(issue.issueKey);
    setError(null);
    const diagnosis = await api.diagnoseIssueCard(issue.cardId);
    if (!diagnosis.ok) {
      setError(diagnosis.message ?? diagnosis.error);
      setAnalyzing(null);
      return false;
    }
    const saved = await api.saveIssueAnalysis(issue.issueKey, diagnosis.data.diagnosis);
    setAnalyzing(null);
    if (!saved.ok) {
      setError(saved.message ?? saved.error);
      return false;
    }
    setItems(
      (current) =>
        current?.map((item) => (item.issueKey === issue.issueKey ? saved.data.issue : item)) ?? [],
    );
    return true;
  };

  const analyzeUnreviewed = async () => {
    const pending = (items ?? []).filter((issue) => issue.analysis === undefined).slice(0, 25);
    for (const issue of pending) {
      if (!(await analyzeIssue(issue))) break;
    }
  };

  const saveReviewerGuidance = async (issue: ChecklistIssue) => {
    const draft = guidanceDrafts[issue.issueKey] ?? issue.reviewerGuidance ?? '';
    setUpdating(issue.issueKey);
    setError(null);
    const result = await api.saveIssueReviewerGuidance(
      issue.issueKey,
      draft.trim() === '' ? null : draft.trim(),
    );
    setUpdating(null);
    if (!result.ok) {
      setError(result.message ?? result.error);
      return;
    }
    setItems(
      (current) =>
        current?.map((item) => (item.issueKey === issue.issueKey ? result.data.issue : item)) ?? [],
    );
    setGuidanceDrafts((current) => {
      const { [issue.issueKey]: _discarded, ...remaining } = current;
      return remaining;
    });
  };

  const unreviewedCount = items?.filter((issue) => issue.analysis === undefined).length ?? 0;

  return (
    <section className="tcvs-checklist-directory" role="tabpanel">
      <div className="tcvs-checklist-directory-header">
        <div>
          <h3>Reported checklist issues</h3>
          <p>Review card-linked checklist defects reported during identity verification.</p>
        </div>
        <div className="tcvs-inline-actions">
          {status === 'OPEN' && unreviewedCount > 0 ? (
            <button
              type="button"
              className="tcvs-btn-compact"
              disabled={analyzing !== null}
              onClick={() => void analyzeUnreviewed()}
            >
              {analyzing === null
                ? `Analyze unreviewed (${Math.min(unreviewedCount, 25)})`
                : 'Analyzing…'}
            </button>
          ) : null}
          <button type="button" className="tcvs-btn-compact" onClick={() => void load()}>
            Refresh
          </button>
        </div>
      </div>
      <div className="tcvs-checklist-issue-status" role="tablist" aria-label="Issue status">
        {(['OPEN', 'RESOLVED'] as const).map((value) => (
          <button
            key={value}
            type="button"
            role="tab"
            className="tcvs-btn-compact"
            aria-selected={status === value}
            disabled={status === value}
            onClick={() => setStatus(value)}
          >
            {value === 'OPEN' ? 'Open' : 'Resolved'}
          </button>
        ))}
      </div>
      {error !== null ? <p className="tcvs-error">Error: {error}</p> : null}
      {items === null ? (
        <p>Loading…</p>
      ) : items.length === 0 ? (
        <p>No {status === 'OPEN' ? 'open' : 'resolved'} checklist issues.</p>
      ) : (
        <div className="tcvs-table-wrap">
          <table className="tcvs-table tcvs-checklist-issues-table">
            <thead>
              <tr>
                <th>Issue</th>
                <th>Triage</th>
                <th>Checklist context</th>
                <th>Inventory</th>
                <th>Reported</th>
              </tr>
            </thead>
            <tbody>
              {items.map((issue) => (
                <tr key={issue.issueKey}>
                  <td>
                    <strong>{checklistIssueTypeLabel(issue.issueType)}</strong>
                    {issue.note === undefined ? null : <div>{issue.note}</div>}
                  </td>
                  <td>
                    {issue.analysis === undefined ? (
                      <span className="tcvs-muted">Not analyzed</span>
                    ) : (
                      <>
                        <strong>{issue.analysis.summary}</strong>
                        <div>{issue.analysis.recommendedAction}</div>
                        {issue.analysis.evidence.length === 0 ? null : (
                          <div className="tcvs-muted">{issue.analysis.evidence.join(' · ')}</div>
                        )}
                      </>
                    )}
                    <div className="tcvs-checklist-issue-triage-actions">
                      <button
                        type="button"
                        className="tcvs-btn-compact"
                        disabled={analyzing !== null || updating === issue.issueKey}
                        onClick={() => void analyzeIssue(issue)}
                      >
                        {checklistIssueAnalysisActionLabel(issue, analyzing === issue.issueKey)}
                      </button>
                      <button
                        type="button"
                        className="tcvs-btn-compact"
                        disabled={updating === issue.issueKey}
                        onClick={() => void changeStatus(issue)}
                      >
                        {updating === issue.issueKey
                          ? 'Saving…'
                          : issue.status === 'OPEN'
                            ? 'Mark resolved'
                            : 'Reopen'}
                      </button>
                    </div>
                    <label>
                      <span>Reviewer guidance / workaround</span>
                      <textarea
                        value={guidanceDrafts[issue.issueKey] ?? issue.reviewerGuidance ?? ''}
                        maxLength={1_000}
                        rows={3}
                        disabled={updating === issue.issueKey}
                        placeholder="Optional reviewer-safe resolution or workaround."
                        onChange={(event) =>
                          setGuidanceDrafts((current) => ({
                            ...current,
                            [issue.issueKey]: event.target.value,
                          }))
                        }
                      />
                    </label>
                    <button
                      type="button"
                      className="tcvs-btn-compact"
                      disabled={updating === issue.issueKey}
                      onClick={() => void saveReviewerGuidance(issue)}
                    >
                      {updating === issue.issueKey ? 'Saving…' : 'Save guidance'}
                    </button>
                  </td>
                  <td>{checklistIssueContextLabel(issue)}</td>
                  <td>
                    <Link to={`/catalog/${encodeURIComponent(issue.cardId)}`}>Open card</Link>
                    {' · '}
                    <Link to={`/batches/${encodeURIComponent(issue.batchId)}/identify`}>
                      Open batch review
                    </Link>
                  </td>
                  <td>
                    {new Date(issue.createdAt).toLocaleString()}
                    <div className="tcvs-muted">
                      {issue.reporterEmail ?? issue.createdBy.slice(0, 12)}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {truncated ? (
        <p className="tcvs-muted">Showing the newest 200 reports for this status.</p>
      ) : null}
    </section>
  );
}

function CurrentChecklistsView({
  items,
  totalCount,
  query,
  loading,
  loadingMore,
  error,
  truncated,
  onQuery,
  onRefresh,
  onLoadMore,
  onUpload,
}: CurrentChecklistsViewProps): JSX.Element {
  const [assignmentChecklistId, setAssignmentChecklistId] = useState<string | null>(null);

  return (
    <section className="tcvs-checklist-directory" role="tabpanel">
      <div className="tcvs-checklist-directory-header">
        <div>
          <h3>Current checklists</h3>
          <p className="tcvs-muted">
            Checklist products currently available for batch and card identification.
          </p>
        </div>
        <div className="tcvs-actions">
          <button
            type="button"
            className="tcvs-btn-secondary"
            disabled={loading}
            onClick={onRefresh}
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
          <button type="button" className="tcvs-button tcvs-button-primary" onClick={onUpload}>
            Upload checklist
          </button>
        </div>
      </div>

      <div className="tcvs-toolbar tcvs-checklist-directory-toolbar">
        <label className="tcvs-field">
          <span>Search checklists</span>
          <input
            type="search"
            value={query}
            placeholder="Year, brand, product, or category"
            onChange={(event) => onQuery(event.target.value)}
          />
        </label>
        <span className="tcvs-muted">
          {totalCount} matching checklist{totalCount === 1 ? '' : 's'} loaded
        </span>
      </div>

      {error !== null ? (
        <div className="tcvs-alert tcvs-alert-danger">
          Could not load current checklists: {error}
          <button type="button" className="tcvs-btn-secondary" onClick={onRefresh}>
            Retry
          </button>
        </div>
      ) : null}

      {truncated ? <p className="tcvs-muted">More search pages are available.</p> : null}

      <div className="tcvs-table-wrap">
        <table className="tcvs-table tcvs-checklist-directory-table">
          <thead>
            <tr>
              <th>Product</th>
              <th>Year</th>
              <th>Brand</th>
              <th>Category</th>
              <th>Listing templates</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5}>Loading current checklists...</td>
              </tr>
            ) : null}
            {!loading && error === null && items.length === 0 ? (
              <tr>
                <td colSpan={5}>
                  {truncated
                    ? 'No matches loaded yet. Continue loading or narrow your search.'
                    : query.trim() === ''
                      ? 'No checklists have been ingested yet.'
                      : 'No checklists match this search.'}
                </td>
              </tr>
            ) : null}
            {!loading
              ? items.map((checklist) => {
                  const assignmentOpen = assignmentChecklistId === checklist.checklistId;
                  return (
                    <Fragment key={checklist.checklistId}>
                      <tr>
                        <td>
                          <strong>{checklist.product.product || '(unnamed product)'}</strong>
                          <span className="tcvs-mono tcvs-checklist-directory-id">
                            {checklist.checklistId}
                          </span>
                        </td>
                        <td>{checklist.product.year || '-'}</td>
                        <td>{checklist.product.brand || '-'}</td>
                        <td>{checklist.category || '-'}</td>
                        <td>
                          <button
                            type="button"
                            className="tcvs-link-button"
                            aria-expanded={assignmentOpen}
                            onClick={() =>
                              setAssignmentChecklistId(
                                assignmentOpen ? null : checklist.checklistId,
                              )
                            }
                          >
                            {assignmentOpen ? 'Close templates' : 'Set templates'}
                          </button>
                        </td>
                      </tr>
                      {assignmentOpen ? (
                        <tr className="tcvs-checklist-template-row">
                          <td colSpan={5}>
                            <ChecklistTemplateAssignmentEditor checklist={checklist} />
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  );
                })
              : null}
          </tbody>
        </table>
      </div>
      {truncated ? (
        <button
          type="button"
          className="tcvs-btn-secondary"
          disabled={loading || loadingMore}
          onClick={onLoadMore}
        >
          {loadingMore ? 'Loading…' : 'Load more'}
        </button>
      ) : null}
    </section>
  );
}

export function templatesAvailableForChecklistAssignment(
  templates: readonly MarketplaceListingTemplate[],
  selectedTemplateIds: readonly string[],
): MarketplaceListingTemplate[] {
  const selected = new Set(selectedTemplateIds);
  return templates
    .filter((template) => template.status !== 'ARCHIVED' || selected.has(template.templateId))
    .sort((left, right) => {
      if (left.status === 'ACTIVE' && right.status !== 'ACTIVE') return -1;
      if (right.status === 'ACTIVE' && left.status !== 'ACTIVE') return 1;
      return left.name.localeCompare(right.name);
    });
}

function ChecklistTemplateAssignmentEditor({
  checklist,
}: {
  checklist: ReviewChecklistProduct;
}): JSX.Element {
  const marketplace = useMarketplaceApi();
  const [templates, setTemplates] = useState<MarketplaceListingTemplate[]>([]);
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<string[]>([]);
  const [revision, setRevision] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    void Promise.all([
      marketplace.listListingTemplates(),
      marketplace.getChecklistTemplateAssignment(checklist.checklistId),
    ]).then(([templateResult, assignmentResult]) => {
      if (!active) return;
      if (!templateResult.ok) {
        setError(templateResult.message ?? templateResult.error);
      } else {
        setTemplates(templateResult.data.templates);
      }
      if (!assignmentResult.ok) {
        setError(assignmentResult.message ?? assignmentResult.error);
      } else {
        setSelectedTemplateIds(assignmentResult.data.assignment.templateIds);
        setRevision(assignmentResult.data.assignment.revision);
      }
      setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [checklist.checklistId, marketplace]);

  const availableTemplates = useMemo(
    () => templatesAvailableForChecklistAssignment(templates, selectedTemplateIds),
    [selectedTemplateIds, templates],
  );

  function toggleTemplate(templateId: string): void {
    setSelectedTemplateIds((current) =>
      current.includes(templateId)
        ? current.filter((id) => id !== templateId)
        : [...current, templateId],
    );
    setNotice(null);
  }

  async function save(): Promise<void> {
    setSaving(true);
    setError(null);
    setNotice(null);
    const response = await marketplace.updateChecklistTemplateAssignment(checklist.checklistId, {
      templateIds: selectedTemplateIds,
      expectedRevision: revision,
    });
    setSaving(false);
    if (!response.ok) {
      setError(
        response.error === 'checklist_template_assignment_conflict'
          ? 'This assignment changed elsewhere. Close and reopen it, then try again.'
          : (response.message ?? response.error),
      );
      return;
    }
    setSelectedTemplateIds(response.data.assignment.templateIds);
    setRevision(response.data.assignment.revision);
    setNotice('Listing template assignment saved.');
  }

  return (
    <div className="tcvs-checklist-template-editor">
      <div>
        <strong>
          Templates for {checklist.product.year} {checklist.product.brand}{' '}
          {checklist.product.product}
        </strong>
        <p className="tcvs-muted">
          Choose the listing templates operators may use for cards from this checklist.
        </p>
      </div>
      {loading ? <p className="tcvs-muted">Loading templates...</p> : null}
      {error !== null ? <div className="tcvs-alert tcvs-alert-danger">{error}</div> : null}
      {notice !== null ? <div className="tcvs-alert tcvs-alert-success">{notice}</div> : null}
      {!loading && availableTemplates.length === 0 ? (
        <p className="tcvs-muted">Create a listing template in Sales before assigning one.</p>
      ) : null}
      <div className="tcvs-checklist-template-options">
        {availableTemplates.map((template) => (
          <label key={template.templateId}>
            <input
              type="checkbox"
              checked={selectedTemplateIds.includes(template.templateId)}
              onChange={() => toggleTemplate(template.templateId)}
            />
            <span>
              <strong>{template.name}</strong>
              <small>
                {template.status.toLowerCase()} · v{template.version}
                {template.description === '' ? '' : ` · ${template.description}`}
              </small>
            </span>
          </label>
        ))}
      </div>
      <div className="tcvs-actions">
        <button
          type="button"
          className="tcvs-button tcvs-button-primary"
          disabled={loading || saving}
          onClick={() => void save()}
        >
          {saving ? 'Saving...' : 'Save template assignment'}
        </button>
      </div>
    </div>
  );
}

export function sortChecklistProducts(
  items: readonly ReviewChecklistProduct[],
): ReviewChecklistProduct[] {
  return [...items].sort((left, right) => {
    const leftLabel = `${left.product.year}\u0000${left.product.brand}\u0000${left.product.product}`;
    const rightLabel = `${right.product.year}\u0000${right.product.brand}\u0000${right.product.product}`;
    return leftLabel.localeCompare(rightLabel, undefined, { numeric: true, sensitivity: 'base' });
  });
}

export function filterChecklistProducts(
  items: readonly ReviewChecklistProduct[],
  query: string,
): ReviewChecklistProduct[] {
  const needle = query.trim().toLocaleLowerCase();
  if (needle === '') return [...items];
  return items.filter((item) =>
    [
      item.product.year,
      item.product.brand,
      item.product.product,
      item.category,
      item.checklistId,
    ].some((value) => value.toLocaleLowerCase().includes(needle)),
  );
}
